import PropTypes from 'prop-types';
import addIndex from "ramda/es/addIndex";
import append from "ramda/es/append";
import clone from "ramda/es/clone";
import compose from "ramda/es/compose";
import filter from "ramda/es/filter";
import intersperse from "ramda/es/intersperse";
import map from "ramda/es/map";
import range from "ramda/es/range";
import values from "ramda/es/values";
import when from "ramda/es/when";
import cx from "ramda-extension/es/cx";
import isNotEmpty from "ramda-extension/es/isNotEmpty";
import React, { Children, cloneElement, Component } from 'react';
import rejectNilChildren from '../../../../utils/rejectNilChildren';
import TableResponsiveChildRow from '../../TableResponsiveRow/TableResponsiveChildRow';
import TableResponsiveRow from '../../TableResponsiveRow/TableResponsiveRow';
class TableBody extends Component {
    state = {
        childRowOpen: {},
        changedRowSpans: {}
    };
    createRowspanDescriptor() {
        const children = rejectNilChildren(this.props.children);
        const descriptor = {};
        Children.forEach(children, (row, rowIndex)=>{
            Children.forEach(row.props.children, (cell, cellIndex)=>{
                if (cell.props.rowSpan && cell.props.rowSpan > 1) {
                    // because responsive child row is added under every responsive row
                    const correctRowIndex = rowIndex * 2;
                    const rowspanRowDescriptor = descriptor[correctRowIndex] || {
                        rowSpans: {}
                    };
                    rowspanRowDescriptor.rowSpans[cellIndex] = cell.props.rowSpan;
                    descriptor[correctRowIndex] = rowspanRowDescriptor;
                    range(correctRowIndex, correctRowIndex + cell.props.rowSpan * 2).forEach(when((rowspannedRowIndex)=>rowspannedRowIndex % 2 === 0, (rowspannedRowIndex)=>{
                        const rowspannedRowDescriptor = clone(descriptor[rowspannedRowIndex]) || {};
                        rowspannedRowDescriptor.rowspannedByRow = correctRowIndex;
                        descriptor[rowspannedRowIndex] = rowspannedRowDescriptor;
                    }));
                }
            });
        });
        return descriptor;
    }
    render() {
        const { className, stripStyle, columnDescriptor, ...other } = this.props;
        const children = rejectNilChildren(this.props.children);
        const stripStyleMap = {
            gray: {
                borderLeft: 'solid 4px #dfdfdf'
            },
            blue: {
                borderLeft: 'solid 4px #45cff8'
            },
            green: {
                borderLeft: 'solid 4px #93d853'
            }
        };
        // TODO optimize generation only for table responsive HIDE mode
        let rowspanRowsDescriptor;
        if (columnDescriptor?.colSpec && values(columnDescriptor.colSpec).some((column)=>!column.visible)) {
            rowspanRowsDescriptor = this.createRowspanDescriptor();
        } else {
            when(isNotEmpty, ()=>this.setState(()=>({
                        changedRowSpans: {}
                    })));
        }
        const rowspanDecorator = (child, index)=>{
            const { changedRowSpans, childRowOpen } = this.state;
            const rowspanRowDescriptor = rowspanRowsDescriptor[index];
            const rowspannedByRow = rowspanRowDescriptor.rowspannedByRow;
            const currentRowspanForRow = rowspanRowDescriptor && {
                [rowspannedByRow]: changedRowSpans[rowspannedByRow]
            };
            return /*#__PURE__*/ cloneElement(child, {
                currentRowspanForRow,
                rowspanRowDescriptor,
                changeRowspanOnClick: (index)=>{
                    const rowWithRowSpan = rowspanRowsDescriptor[rowspannedByRow];
                    const newChangedRowSpans = clone(changedRowSpans);
                    let rowspanStateForRow = newChangedRowSpans[rowspannedByRow] || rowWithRowSpan.rowSpans[0];
                    rowspanStateForRow = childRowOpen[index] ? rowspanStateForRow - 1 : rowspanStateForRow + 1;
                    newChangedRowSpans[rowspannedByRow] = rowspanStateForRow;
                    this.setState(()=>({
                            changedRowSpans: newChangedRowSpans
                        }));
                }
            });
        };
        const columnDescriptorDecoratorForRow = (child)=>/*#__PURE__*/ cloneElement(child, {
                columnDescriptor,
                onResponsiveClick: (index)=>{
                    const oldOpen = clone(this.state.childRowOpen);
                    oldOpen[index] = !oldOpen[index];
                    this.setState(()=>({
                            childRowOpen: oldOpen
                        }));
                }
            });
        const columnDescriptorDecoratorForColumn = (isResponsive)=>function(child, index) {
                const foundDescriptor = columnDescriptor?.colSpec?.[index];
                return /*#__PURE__*/ cloneElement(child, {
                    responsiveMode: isResponsive,
                    descriptor: foundDescriptor
                });
            };
        const mapIndexed = addIndex(map);
        const withKey = (el, index)=>{
            const cloneWithProps = {
                key: el.key || index,
                rowOrder: index
            };
            if (el.type.displayName === TableResponsiveChildRow.displayName) {
                let rowChildren = Children.toArray(children);
                if (rowChildren[0]) {
                    rowChildren = rowChildren[0].props.children;
                    // there can be only one child, make it array anyway
                    rowChildren = Array.isArray(rowChildren) ? rowChildren : [
                        rowChildren
                    ];
                    cloneWithProps.descriptor = columnDescriptor;
                } else {
                    rowChildren = [];
                }
                rowChildren = compose(filter((child)=>child.props.descriptor ? child.props.descriptor.visible === false : true), mapIndexed(columnDescriptorDecoratorForColumn(true)))(rowChildren);
                const isAnyColumnHidden = columnDescriptor?.colSpec?.findIndex((column)=>!column.visible);
                let isRowOpenForChild = this.state.childRowOpen[index - 1];
                if (isRowOpenForChild && isAnyColumnHidden === -1) {
                    // we can mutate we don't need to rerender because we are in render phase
                    isRowOpenForChild = false;
                /* this.state.childRowOpen[index - 1] = isRowOpenForChild; */ }
                cloneWithProps.open = isRowOpenForChild;
                cloneWithProps.children = rowChildren;
            }
            return /*#__PURE__*/ cloneElement(el, cloneWithProps);
        };
        const specialRow = /*#__PURE__*/ React.createElement(TableResponsiveChildRow, {
            open: true
        });
        const makeRowsResponsive = compose(mapIndexed(withKey), mapIndexed(when((child, index)=>child.type.displayName === TableResponsiveRow.displayName && rowspanRowsDescriptor && rowspanRowsDescriptor[index], rowspanDecorator)), append(specialRow), intersperse(specialRow), map(columnDescriptorDecoratorForRow));
        return /*#__PURE__*/ React.createElement("tbody", {
            className: cx('Table-group', className),
            ...other,
            style: stripStyleMap[stripStyle]
        }, makeRowsResponsive(Children.toArray(children)));
    }
}
TableBody.propTypes = {
    /**
	 * Children will be rendered within the `TableBody`.
	 */ children: PropTypes.node,
    /**
	 * Custom `className` for the root.
	 */ className: PropTypes.string,
    /**
	 * Descriptor of all columns (generated from header) which is added to the component on Table level.
	 */ columnDescriptor: PropTypes.object,
    /**
	 * Strip style
	 */ stripStyle: PropTypes.string
};
TableBody.displayName = 'TableBody';
export default TableBody;
